import { Box, Button, CircularProgress, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Snackbar, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminGetTip, createTip, updateTip } from '../../api/tips';
import { MarkdownView } from '../../components/MarkdownView';
import { buildSocialPost } from '../../common/markdown';
import { env } from '../../env';
import { useApiCallWithAuth } from '../../hooks/useApiCallWithAuth';

interface TipFormState {
  title: string;
  date: string;
  tipContent: string;
  isPublished: boolean;
  domain: string;
}

function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const empty: TipFormState = {
  title: '',
  date: todayLocal(),
  tipContent: '',
  isPublished: true,
  domain: 'General',
};

export function TipEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const tipId = id ? parseInt(id, 10) : null;

  const { request: getRequest } = useApiCallWithAuth(adminGetTip);
  const { request: createRequest } = useApiCallWithAuth(createTip);
  const { request: updateRequest } = useApiCallWithAuth(updateTip);

  const [form, setForm] = useState<TipFormState>(empty);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copyNotice, setCopyNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || tipId === null || isNaN(tipId)) return;
    getRequest(tipId)
      .then(data => {
        setForm({
          title: data.title ?? '',
          date: data.date ?? todayLocal(),
          tipContent: data.tipContent,
          isPublished: data.isPublished ?? true,
          domain: data.domain ?? 'General',
        });
      })
      .catch(() => setError('Failed to load tip'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipId, isEdit]);

  function set(field: keyof TipFormState, value: string) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSave() {
    setError(null);
    if (!form.title.trim() || !form.date.trim() || !form.tipContent.trim()) {
      setError('Title, date and tip content are required.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title,
        date: form.date,
        tipContent: form.tipContent,
        isPublished: form.isPublished,
        domain: form.domain,
      };

      if (isEdit && tipId !== null && !isNaN(tipId)) {
        await updateRequest(tipId, payload);
      } else {
        await createRequest(payload);
      }
      navigate('/tips/admin');
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  // Facebook and LinkedIn composers are plain text, so markdown syntax would paste
  // through literally. Flatten it before putting it on the clipboard.
  async function handleCopyForSocial() {
    const publicUrl = isEdit && tipId !== null && !isNaN(tipId) && form.isPublished
      ? `${env.publicSiteUrl}/tips/${tipId}`
      : null;

    const text = buildSocialPost({ title: form.title, markdown: form.tipContent, url: publicUrl });

    try {
      await navigator.clipboard.writeText(text);
      setCopyNotice(publicUrl
        ? 'Copied as plain text with link — paste straight into Facebook or LinkedIn.'
        : 'Copied as plain text. Save and publish the tip to include its link.');
    } catch {
      setCopyNotice('Could not copy to the clipboard. Check the browser permissions and try again.');
    }
  }

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 3 }}>
        <Typography variant='h5' fontWeight={700}>{isEdit ? 'Edit Tip' : 'New Tip'}</Typography>
        <Stack direction='row' spacing={1} alignItems='flex-start'>
          <Tooltip title='Copy the tip as plain text for Facebook or LinkedIn'>
            <span>
              <Button
                variant='outlined'
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyForSocial}
                disabled={!form.tipContent.trim()}
              >
                Copy for Social
              </Button>
            </span>
          </Tooltip>
          <Button variant='outlined' onClick={() => setPreview(p => !p)}>
            {preview ? 'Edit' : 'Preview'}
          </Button>
          <Button variant='outlined' onClick={() => navigate('/tips/admin')}>Cancel</Button>
          <Button variant='contained' onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Stack>
      </Stack>

      {error && <Typography color='error' sx={{ mb: 2 }}>{error}</Typography>}

      {preview ? (
        <Box>
          <Typography variant='h4' fontWeight={700} sx={{ mb: 0.5 }}>
            {form.title || '(No title)'}
          </Typography>
          <Typography variant='subtitle1' color='text.secondary' sx={{ mb: 2 }}>
            {new Date(`${form.date}T00:00:00`).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
          <MarkdownView>{form.tipContent || '(No content)'}</MarkdownView>
        </Box>
      ) : (
        <Stack spacing={2.5}>
          <TextField
            label='Title'
            value={form.title}
            onChange={e => set('title', e.target.value)}
            fullWidth
            required
          />

          <TextField
            label='Date'
            type='date'
            value={form.date}
            onChange={e => set('date', e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />

          <FormControl fullWidth>
            <InputLabel id='domain-label'>Domain</InputLabel>
            <Select
              labelId='domain-label'
              value={form.domain}
              label='Domain'
              onChange={e => set('domain', e.target.value)}
            >
              <MenuItem value='General'>General</MenuItem>
              <MenuItem value='Sports'>Sports</MenuItem>
              <MenuItem value='Running'>Running</MenuItem>
              <MenuItem value='Food'>Food</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={form.isPublished}
                onChange={e => setForm(f => ({ ...f, isPublished: e.target.checked }))}
              />
            }
            label={form.isPublished ? 'Published' : 'Draft'}
          />

          <TextField
            label='Tip Content (Markdown)'
            value={form.tipContent}
            onChange={e => set('tipContent', e.target.value)}
            fullWidth
            multiline
            rows={12}
            required
            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.875rem' } }}
            helperText='Markdown supported: **bold**, _italics_, # headings, - lists, [links](url), and tables.'
          />
        </Stack>
      )}

      <Snackbar
        open={copyNotice !== null}
        autoHideDuration={4000}
        onClose={() => setCopyNotice(null)}
        message={copyNotice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
